"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _getNavigationTiming = _interopRequireDefault(require("../utilities/getNavigationTiming"));

var _requestIdleCallback = _interopRequireDefault(require("../utilities/requestIdleCallback"));

/**
 * A plugin responsible for collecting metrics exposed by the
 * [Navigation Timing][1] specification.
 *
 * This plugin adds the following parameters to the beacon:
 *
 * - `connectEnd`: A `number` representing the time immediately after the
 *   browser finishes establishing the connection to the server to retrieve
 *   the resource.
 *
 * - `connectStart`: A `number` representing the time immediately before the
 *   browser starts to establish the connection to the server to retrieve
 *   the resource.
 *
 * - `domainLookupEnd`: A `number` representing the time immediately after the
 *   browser finishes the domain name lookup for the resource.
 *
 * - `domainLookupStart`: A `number` representing the time immediately before
 *   the browser starts the domain name lookup for the resource.
 *
 * - `domComplete`: A `number` representing a time value equal to the time
 *   immediately before the browser sets the current document readiness of the
 *   current document to complete.
 *
 * - `domContentLoadedEventEnd`: A `number` representing the time value equal
 *   to the time immediately after the current document's `DOMContentLoaded`
 *   event completes.
 *
 * - `domContentLoadedEventStart`: A `number` representing the time value equal
 *   to the time immediately before the user agent fires the `DOMContentLoaded`
 *   event at the current document.
 *
 * - `domInteractive`: A `number` representing the time value equal to the time
 *   immediately before the user agent sets the current document readiness of
 *   the current document to interactive.
 *
 * - `fetchStart`: A `numnber` representing the time immediately before the
 *   browser starts to fetch the HTML document.
 *
 * - `loadEventEnd`: A `number` representing the time when the load
 *   event of the current document is completed.
 *
 * - `loadEventStart`: A `number` representing the time value equal to the time
 *   immediately before the load event of the current document is fired.
 *
 * - `location`: A `string` representing the HTML document address.
 *
 * - `navigationType`: A `string` representing the navigation type. Can be
 *   one of: `"navigate"`, `"reload"`, `"back_forward"` or `"prerender"`.
 *
 * - `redirectEnd`: A `number` representing the time immediately after
 *   receiving the last byte of the response of the last redirect.
 *
 * - `redirectStart`: A `number` representing the time immediately before the
 *   browser starts to fetch the resource.
 *
 * - `requestStart`: A `number` representing the time immediately before the
 *   browser starts requesting the resource from the server.
 *
 * - `responseEnd`: A `number` representing the time immediately after the
 *   browser receives the last byte of the resource or immediately before the
 *   transport connection is closed, whichever comes first.
 *
 * - `responseStart`: A `number` representing the time immediately after the
 *   browser receives the first byte of the response from the server.
 *
 * - `secureConnectionStart`: A `number` representing the time immediately
 *   before the browser starts the handshake process to secure the current
 *   connection.
 *
 * - `startTime`: A `number` representing the timestamp for the time a
 *   resource fetch started.
 *
 * - `unloadEventEnd`: A `number` representing the time value equal to the time
 *   immediately after the user agent finishes the unload event of the
 *   previous document.
 *
 * - `unloadEventStart`: A `number` representing the time value equal to the
 *   time immediately before the user agent starts the unload event of the
 *   previous document.
 *
 * [1]: https://www.w3.org/TR/navigation-timing/
 */
var createNavigationTimingPlugin = function createNavigationTimingPlugin() {
  return function (_ref) {
    var addVariable = _ref.addVariable,
        sendBeacon = _ref.sendBeacon;
    var isComplete = false;
    return {
      beaconShouldSendData: function beaconShouldSendData() {
        return isComplete;
      },
      pageDidLoad: function pageDidLoad() {
        // Queue task to ensure variables are captured after `loadEventEnd`.
        (0, _requestIdleCallback.default)(function () {
          try {
            var navigationTiming = (0, _getNavigationTiming.default)();

            if (navigationTiming != null) {
              addVariable('connectEnd', navigationTiming.connectEnd, true);
              addVariable('connectStart', navigationTiming.connectStart, true);
              addVariable('domainLookupEnd', navigationTiming.domainLookupEnd, true);
              addVariable('domainLookupStart', navigationTiming.domainLookupStart, true);
              addVariable('domComplete', navigationTiming.domComplete, true);
              addVariable('domContentLoadedEventEnd', navigationTiming.domContentLoadedEventEnd, true);
              addVariable('domContentLoadedEventStart', navigationTiming.domContentLoadedEventStart, true);
              addVariable('domInteractive', navigationTiming.domInteractive, true);
              addVariable('fetchStart', navigationTiming.fetchStart, true);
              addVariable('loadEventEnd', navigationTiming.loadEventEnd, true);
              addVariable('loadEventStart', navigationTiming.loadEventStart, true);
              addVariable('location', navigationTiming.name, true);
              addVariable('navigationType', navigationTiming.type, true);
              addVariable('redirectEnd', navigationTiming.redirectEnd, true);
              addVariable('redirectStart', navigationTiming.redirectStart, true);
              addVariable('requestStart', navigationTiming.requestStart, true);
              addVariable('responseEnd', navigationTiming.responseEnd, true);
              addVariable('responseStart', navigationTiming.responseStart, true);
              addVariable('secureConnectionStart', navigationTiming.secureConnectionStart, true);
              addVariable('startTime', navigationTiming.startTime, true);
              addVariable('unloadEventEnd', navigationTiming.unloadEventEnd, true);
              addVariable('unloadEventStart', navigationTiming.unloadEventStart, true);
            }
          } catch (error) {// Do nothing.
          } finally {
            isComplete = true;
            sendBeacon();
          }
        });
      }
    };
  };
};

var _default = createNavigationTimingPlugin;
exports.default = _default;